/**
 * SDK Provider
 *
 * Provides React Query client and API client to the application. The
 * QueryClient built here is the single source of fetch-state for the
 * whole app — replace it at your peril, and NEVER mutate it directly
 * from callers. Global error handling hooks (`onQueryError`,
 * `onMutationError`) are the sanctioned extension point.
 */

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { createTraceIdInterceptor } from '@tonnie/observability';

// Derive the onError callback signatures straight from the library's
// own constructor argument shape. Across react-query minor versions
// the mutation handler's argument order has shifted (e.g. whether
// `context` is the 3rd or 5th arg). Borrowing the library's own type
// keeps us version-proof without hand-rolling positional args.
type QueryCacheCtor = ConstructorParameters<typeof QueryCache>[0];
type MutationCacheCtor = ConstructorParameters<typeof MutationCache>[0];
type QueryErrorHandler = NonNullable<QueryCacheCtor>['onError'];
type MutationErrorHandler = NonNullable<MutationCacheCtor>['onError'];
import { TonnieClient, createClient, TonnieApiError } from '../client/base';
import { SdkConfig, createSdkConfig } from '../client/config';

interface SdkContextValue {
  client: TonnieClient;
  config: SdkConfig;
}

const SdkContext = createContext<SdkContextValue | null>(null);

export interface SdkProviderProps {
  children: ReactNode;
  config?: Partial<SdkConfig>;
  /**
   * Caller-supplied QueryClient takes precedence over the built-in
   * one. In most apps you do NOT need this — pass `onQueryError` /
   * `onMutationError` instead and let the SDK build a QueryClient with
   * those handlers wired in.
   */
  queryClient?: QueryClient;
  /**
   * Optional getter for the active correlation id. When provided, the
   * SDK attaches `x-trace-id` to every outbound request, threading the
   * id from FE actions into platform-api logs and brain-service logs.
   *
   * Apps wire this from `<CopilotCorrelationProvider>` (journey-web)
   * or any equivalent context that mints ids per user-action. When
   * the getter returns undefined, the SDK falls back to a per-request
   * generated id — still useful but not linked across calls.
   *
   * Required by the platform's QA logging contract (radar 2026-04-28
   * — Logging Phase L-2). Pre-fix, FE Sentry events and platform-api
   * logs for the same user action lived as separate islands; QA had
   * to correlate by `(flowId, journeyId, timestamp)` tuples.
   */
  getCorrelationId?: () => string | undefined;
  /**
   * Global query-error handler — fires for every failed `useQuery` /
   * `useInfiniteQuery`. The SDK wires this into `QueryCache.onError`
   * so there's ONE place app-level error infra (toast, banner, Sentry
   * capture) can hook. Only runs when a custom `queryClient` is NOT
   * supplied.
   *
   * In practice the `error` will be a `TonnieApiError` thrown by the
   * SDK base client. Cast with `error instanceof TonnieApiError`
   * inside the handler.
   */
  onQueryError?: QueryErrorHandler;
  /**
   * Global mutation-error handler. Fires for every failed
   * `useMutation`. Same choke-point as onQueryError for mutations.
   */
  onMutationError?: MutationErrorHandler;
  /**
   * Predicate consulted by the retry policy BEFORE incrementing
   * failureCount. Return true to globally pause retries — used by the
   * citizen-mobile circuit breaker to short-circuit retry storms
   * during a sustained outage. Safe to leave undefined; the SDK
   * falls back to count-based retry only.
   */
  shouldPauseRetries?: () => boolean;
}

/**
 * Build a QueryClient with global error-cache wiring + smart retry
 * policy. Exposed for tests + for advanced consumers that want to
 * configure additional defaults on top; most callers should let
 * `SdkProvider` build it internally.
 */
export function buildSdkQueryClient(
  onQueryError?: QueryErrorHandler,
  onMutationError?: MutationErrorHandler,
  shouldPauseRetries?: () => boolean
): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: onQueryError,
    }),
    mutationCache: new MutationCache({
      onError: onMutationError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60, // 1 minute
        gcTime: 1000 * 60 * 5, // 5 minutes (formerly cacheTime)
        // Retry policy — intentionally smart rather than blanket 3x:
        //   - Circuit breaker open → abandon immediately (citizen-
        //     mobile wires this to the network-failure circuit
        //     breaker so a sustained outage doesn't trigger a retry
        //     storm across every screen's queries).
        //   - 4xx client errors (except timeout) → NEVER retry (user
        //     got what they asked for; retrying won't change the
        //     answer and creates noise in audit / analytics).
        //   - Network errors → ONE retry only. They fail fast
        //     (<100ms) so piling on retries just floods DevTools;
        //     one quick retry catches the transient cases.
        //   - 5xx / timeouts → up to 3 retries (these can genuinely
        //     clear on their own).
        retry: (failureCount, error) => {
          if (shouldPauseRetries?.()) return false;
          if (error instanceof TonnieApiError) {
            // 429 Too Many Requests is a TRANSIENT back-pressure signal
            // (the server is asking us to slow down, with a Retry-After
            // hint) — NOT a "client got what they asked for" 4xx. Retry it
            // with backoff so a brief rate-limit spike degrades into a short
            // delay instead of a broken screen (e.g. a blank dashboard or a
            // false "no journey" guard). Checked BEFORE the generic 4xx rule.
            if (error.status === 429) {
              return failureCount < 3;
            }
            if (error.status >= 400 && error.status < 500 && !error.isTimeout) {
              return false;
            }
            if (error.isNetworkError) {
              return failureCount < 1;
            }
          }
          return failureCount < 3;
        },
        // Network errors get a longer first-retry delay to let a brief
        // outage actually clear; 5xx/timeouts use the classic 1s/2s/4s.
        retryDelay: (attemptIndex, error) => {
          if (error instanceof TonnieApiError && error.isNetworkError) {
            return Math.min(5000 * 2 ** attemptIndex, 30000);
          }
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Mutations never auto-retry — they're usually non-idempotent
        // and retrying a POST transparently can double-charge, etc.
        retry: 0,
      },
    },
  });
}

export function SdkProvider({
  children,
  config: configInput,
  queryClient,
  onQueryError,
  onMutationError,
  shouldPauseRetries,
  getCorrelationId,
}: SdkProviderProps): ReactNode {
  const config = useMemo(() => createSdkConfig(configInput), [configInput]);
  const client = useMemo(() => {
    const c = createClient(config);
    // Attach the trace-id interceptor so every outbound request
    // carries `x-trace-id`. The getter is called PER REQUEST so
    // updates from `<CopilotCorrelationProvider>.beginAction()`
    // take effect immediately without re-creating the client.
    c.addRequestInterceptor(createTraceIdInterceptor(getCorrelationId));
    return c;
  }, [config, getCorrelationId]);

  // Build the QueryClient with the caller's error handlers, OR use the
  // explicit one the caller passed in (which is responsible for its own
  // handlers). Memoised on the handlers so we don't recreate the cache
  // on every render — React Query's cache IS the app's fetch state, so
  // losing it breaks every in-flight query.
  const defaultClient = useMemo(
    () => buildSdkQueryClient(onQueryError, onMutationError, shouldPauseRetries),
    [onQueryError, onMutationError, shouldPauseRetries]
  );

  const contextValue: SdkContextValue = useMemo(() => ({ client, config }), [client, config]);

  return (
    <SdkContext.Provider value={contextValue}>
      <QueryClientProvider client={queryClient ?? defaultClient}>{children}</QueryClientProvider>
    </SdkContext.Provider>
  );
}

export function useSdkContext(): SdkContextValue {
  const context = useContext(SdkContext);
  if (!context) {
    throw new Error('useSdkContext must be used within an SdkProvider');
  }
  return context;
}

export function useApiClient(): TonnieClient {
  const { client } = useSdkContext();
  return client;
}
